/**
 * Phase 9B.2 — Role-specific navigation configuration.
 *
 * Three builders, all pure functions:
 *
 *   buildDesktopNav(role, caps, ctx, t)   → grouped rail (Owner/Manager/Teamlead/Employee)
 *   buildMobileBottomNav(role, caps, t)   → flat array, MAX 5 items
 *   buildMoreDrawerItems(role, caps, t)   → grouped overflow for the "Meer" sheet
 *
 * The `ctx` argument carries runtime feature flags (documentsEnabled,
 * handbookNew, …) so the nav config can react without importing them.
 *
 * Rules honoured here:
 *
 *  • Never render disabled items — RoleGate/capability keeps them
 *    absent when RBAC would deny.
 *  • Personal account destinations (Profile, Voorkeuren, Handboek)
 *    are available for every role via the Personal section of the
 *    rail and the More drawer on mobile. See PHASE_9_HANDOFF §7.
 *  • `/roster` is the canonical scheduling destination for planners.
 *    `/team-today` and `/quick-assign` intentionally live under
 *    "Meer" — the routes stay reachable until 9B.3 absorbs them.
 *  • Employee mobile bottom nav is capped at 5 items. This is a
 *    hard invariant tested in RoleNav.test.jsx.
 */
import {
  LayoutDashboard, CalendarDays, Users, MapPin, Building2, ShieldCheck,
  ClipboardList, Repeat, Clock, Bell, User, LogOut, Settings as SettingsIcon, Coffee,
  Megaphone, FileBarChart, FolderOpen, BookOpen, CreditCard, Zap, Wallet,
  TrendingUp, Home, MoreHorizontal,
} from "lucide-react";

/* ------------------------------------------------------------------ */
/*  Desktop rails                                                     */
/* ------------------------------------------------------------------ */

function ownerRail(caps, ctx, t) {
  return [
    {
      key: "planning",
      section: t("navsec_planning"),
      items: [
        { to: "/dashboard",  label: t("dashboard"), icon: LayoutDashboard, testid: "nav-dashboard" },
        { to: "/roster",     label: t("roster"),    icon: CalendarDays,    testid: "nav-roster" },
      ],
    },
    {
      key: "team",
      section: t("navsec_team"),
      items: [
        { to: "/employees",   label: t("employees"), icon: Users,     testid: "nav-employees" },
        { to: "/teams",       label: t("teams"),     icon: Building2, testid: "nav-teams" },
        { to: "/locations",   label: t("locations"), icon: MapPin,    testid: "nav-locations" },
        { to: "/permissions", label: t("permissions_nav"), icon: ShieldCheck, testid: "nav-permissions" },
      ],
    },
    {
      key: "time_absence",
      section: t("navsec_time_absence"),
      items: [
        { to: "/requests",   label: t("leave_requests"), icon: ClipboardList, testid: "nav-requests" },
        { to: "/swaps",      label: t("swap_requests"),  icon: Repeat,        testid: "nav-swaps" },
        { to: "/timesheets", label: t("timesheets"),     icon: Clock,         testid: "nav-timesheets" },
      ],
    },
    {
      key: "insights",
      section: t("navsec_insights_v2"),
      items: [
        { to: "/reports", label: t("reports_label"), icon: FileBarChart, testid: "nav-reports" },
        { to: "/payroll", label: t("payroll_label"), icon: Wallet,       testid: "nav-payroll" },
      ],
    },
    {
      key: "communication",
      section: t("navsec_communication"),
      items: [
        ...(ctx.documentsEnabled
          ? [{ to: "/documents", label: t("documents_label"), icon: FolderOpen, testid: "nav-documents" }]
          : []),
        { to: "/news",     label: t("news"),     icon: Megaphone, testid: "nav-news" },
        { to: "/handbook", label: t("handbook"), icon: BookOpen,  testid: "nav-handbook", handbookNewBadge: true },
      ],
    },
    {
      key: "admin",
      section: t("navsec_admin"),
      items: [
        { to: "/settings",       label: t("settings"),         icon: SettingsIcon, testid: "nav-settings" },
        { to: "/pause-settings", label: t("pause_nav_label"),  icon: Coffee,       testid: "nav-pause-settings" },
        { to: "/billing",        label: t("billing_label"),    icon: CreditCard,   testid: "nav-billing" },
      ],
    },
  ];
}

function managerRail(caps, ctx, t) {
  return [
    {
      key: "planning",
      section: t("navsec_planning"),
      items: [
        { to: "/dashboard", label: t("dashboard"), icon: LayoutDashboard, testid: "nav-dashboard" },
        { to: "/roster",    label: t("roster"),    icon: CalendarDays,    testid: "nav-roster" },
      ],
    },
    {
      key: "team",
      section: t("navsec_team"),
      items: [
        { to: "/employees",   label: t("employees"), icon: Users,     testid: "nav-employees" },
        { to: "/teams",       label: t("teams"),     icon: Building2, testid: "nav-teams" },
        ...(caps.canPermissions
          ? [{ to: "/permissions", label: t("permissions_nav"), icon: ShieldCheck, testid: "nav-permissions" }]
          : []),
      ],
    },
    {
      key: "time_absence",
      section: t("navsec_time_absence"),
      items: [
        { to: "/requests",   label: t("leave_requests"), icon: ClipboardList, testid: "nav-requests" },
        { to: "/swaps",      label: t("swap_requests"),  icon: Repeat,        testid: "nav-swaps" },
        { to: "/timesheets", label: t("timesheets"),     icon: Clock,         testid: "nav-timesheets" },
      ],
    },
    {
      key: "insights",
      section: t("navsec_insights_v2"),
      items: [
        { to: "/reports", label: t("reports_label"), icon: FileBarChart, testid: "nav-reports" },
        ...(caps.canPayroll
          ? [{ to: "/payroll", label: t("payroll_label"), icon: Wallet, testid: "nav-payroll" }]
          : []),
      ],
    },
    {
      key: "communication",
      section: t("navsec_communication"),
      items: [
        ...(ctx.documentsEnabled
          ? [{ to: "/documents", label: t("documents_label"), icon: FolderOpen, testid: "nav-documents" }]
          : []),
        { to: "/news",     label: t("news"),     icon: Megaphone, testid: "nav-news" },
        { to: "/handbook", label: t("handbook"), icon: BookOpen,  testid: "nav-handbook", handbookNewBadge: true },
      ],
    },
    {
      key: "my_work",
      section: t("navsec_my_work"),
      items: [
        { to: "/my-roster", label: t("my_roster"), icon: CalendarDays,  testid: "nav-my-roster" },
        { to: "/my-leave",  label: t("my_leave"),  icon: ClipboardList, testid: "nav-my-leave" },
        { to: "/my-swaps",  label: t("my_swaps"),  icon: Repeat,        testid: "nav-my-swaps" },
      ],
    },
    // Admin — capability-driven. Manager sees pause-settings when
    // canPauseSettings is true; owner-only surfaces (locations,
    // /settings, /billing) never appear on this rail.
    ...(caps.canPauseSettings ? [{
      key: "admin",
      section: t("navsec_admin"),
      items: [
        { to: "/pause-settings", label: t("pause_nav_label"), icon: Coffee, testid: "nav-pause-settings" },
      ],
    }] : []),
  ];
}

function teamleadRail(caps, ctx, t) {
  return [
    {
      key: "day_ops",
      section: t("navsec_day_ops"),
      items: [
        { to: "/dashboard", label: t("dashboard"), icon: LayoutDashboard, testid: "nav-dashboard" },
        { to: "/roster",    label: t("roster"),    icon: CalendarDays,    testid: "nav-roster" },
      ],
    },
    {
      key: "team",
      section: t("navsec_team"),
      items: [
        { to: "/employees", label: t("employees"), icon: Users,     testid: "nav-employees" },
        { to: "/teams",     label: t("teams"),     icon: Building2, testid: "nav-teams" },
      ],
    },
    {
      key: "time_absence",
      section: t("navsec_time_absence"),
      items: [
        { to: "/requests",   label: t("leave_requests"), icon: ClipboardList, testid: "nav-requests" },
        { to: "/swaps",      label: t("swap_requests"),  icon: Repeat,        testid: "nav-swaps" },
        { to: "/timesheets", label: t("timesheets"),     icon: Clock,         testid: "nav-timesheets" },
      ],
    },
    {
      key: "insights",
      section: t("navsec_insights_v2"),
      items: [
        { to: "/reports", label: t("reports_label"), icon: FileBarChart, testid: "nav-reports" },
      ],
    },
    {
      key: "communication",
      section: t("navsec_communication"),
      items: [
        ...(ctx.documentsEnabled
          ? [{ to: "/documents", label: t("documents_label"), icon: FolderOpen, testid: "nav-documents" }]
          : []),
        { to: "/news",     label: t("news"),     icon: Megaphone, testid: "nav-news" },
        { to: "/handbook", label: t("handbook"), icon: BookOpen,  testid: "nav-handbook", handbookNewBadge: true },
      ],
    },
    {
      key: "my_work",
      section: t("navsec_my_work"),
      items: [
        { to: "/my-roster",       label: t("my_roster"),       icon: CalendarDays,  testid: "nav-my-roster" },
        { to: "/my-leave",        label: t("my_leave"),        icon: ClipboardList, testid: "nav-my-leave" },
        { to: "/my-swaps",        label: t("my_swaps"),        icon: Repeat,        testid: "nav-my-swaps" },
        { to: "/my-availability", label: t("my_availability"), icon: Clock,         testid: "nav-my-availability" },
      ],
    },
  ];
}

function employeeRail(caps, ctx, t) {
  return [
    {
      key: "my_work",
      section: t("navsec_my_work"),
      items: [
        { to: "/my-overview",     label: t("my_overview_nav") || t("home"), icon: Home,          testid: "nav-my-overview" },
        { to: "/my-roster",       label: t("my_roster"),                    icon: CalendarDays,  testid: "nav-my-roster" },
        { to: "/my-leave",        label: t("my_leave"),                     icon: ClipboardList, testid: "nav-my-leave" },
        { to: "/my-swaps",        label: t("my_swaps"),                     icon: Repeat,        testid: "nav-my-swaps" },
        { to: "/my-availability", label: t("my_availability"),              icon: Clock,         testid: "nav-my-availability" },
      ],
    },
    {
      key: "communication",
      section: t("navsec_communication"),
      items: [
        ...(ctx.documentsEnabled
          ? [{ to: "/documents", label: t("documents_label"), icon: FolderOpen, testid: "nav-documents" }]
          : []),
        { to: "/news",          label: t("news"),          icon: Megaphone, testid: "nav-news" },
        { to: "/notifications", label: t("notifications"), icon: Bell,      testid: "nav-notifications" },
        { to: "/handbook",      label: t("handbook"),      icon: BookOpen,  testid: "nav-handbook", handbookNewBadge: true },
      ],
    },
  ];
}

/**
 * Return the grouped desktop rail for the given role. When the role
 * is unknown the rail is empty — the shell handles the null state
 * (booting/logout) before ever calling this.
 */
export function buildDesktopNav(caps, ctx, t) {
  if (caps.isOwner)    return ownerRail(caps, ctx, t);
  if (caps.isManager)  return managerRail(caps, ctx, t);
  if (caps.isTeamlead) return teamleadRail(caps, ctx, t);
  if (caps.isEmployee) return employeeRail(caps, ctx, t);
  return [];
}

/* ------------------------------------------------------------------ */
/*  Mobile bottom navigation (max 5)                                  */
/* ------------------------------------------------------------------ */

/**
 * MAX 5 items per role. Notifications live in the mobile header bell
 * (not duplicated in the bar) to avoid dual entry points.
 *
 *   Owner / Manager / Teamlead: Rooster · Verzoeken · Mijn rooster · Documenten · Meer
 *   Employee: Home · Rooster · Verlof · Documenten · Meer
 */
export function buildMobileBottomNav(caps, ctx, t) {
  if (caps.isEmployee) {
    const nav = [
      { to: "/my-overview", label: t("home"),           icon: Home,          testid: "bnav-home" },
      { to: "/my-roster",   label: t("my_roster"),      icon: CalendarDays,  testid: "bnav-my-roster" },
      { to: "/my-leave",    label: t("my_leave"),       icon: ClipboardList, testid: "bnav-my-leave" },
    ];
    if (ctx.documentsEnabled) {
      nav.push({ to: "/documents", label: t("documents_label"), icon: FolderOpen, testid: "bnav-documents" });
    } else {
      nav.push({ to: "/my-swaps", label: t("my_swaps"), icon: Repeat, testid: "bnav-my-swaps" });
    }
    nav.push({ to: "__more__", label: t("more_nav"), icon: MoreHorizontal, testid: "bnav-more" });
    return nav.slice(0, 5);
  }

  if (caps.isPlanner) {
    const nav = [
      { to: "/roster",           label: t("roster"),          icon: CalendarDays,  testid: "bnav-roster" },
      { to: "/manager-requests", label: t("requests_nav"),    icon: ClipboardList, testid: "bnav-manager-requests" },
      { to: "/my-roster",        label: t("my_roster"),       icon: Home,          testid: "bnav-my-roster" },
    ];
    if (ctx.documentsEnabled) {
      nav.push({ to: "/documents", label: t("documents_label"), icon: FolderOpen, testid: "bnav-documents" });
    } else {
      nav.push({ to: "/news", label: t("news"), icon: Megaphone, testid: "bnav-news" });
    }
    nav.push({ to: "__more__", label: t("more_nav"), icon: MoreHorizontal, testid: "bnav-more" });
    return nav.slice(0, 5);
  }

  return [];
}

/* ------------------------------------------------------------------ */
/*  More drawer                                                       */
/* ------------------------------------------------------------------ */

/**
 * Grouped list of items surfaced through the "Meer" bottom-sheet on
 * mobile. Includes personal account destinations (Profile, Voorkeuren)
 * for every role, plus any secondary planner surfaces (Team vandaag,
 * Snel toewijzen) for planners. Logout is rendered by MoreDrawer itself.
 */
export function buildMoreDrawerItems(caps, ctx, t) {
  const groups = [];

  // Planner-specific secondary destinations
  if (caps.isPlanner) {
    groups.push({
      key: "operations",
      section: t("navsec_day_ops"),
      items: [
        { to: "/team-today",   label: t("team_today_title"),  icon: Users,   testid: "more-team-today" },
        { to: "/quick-assign", label: t("quick_assign_title"), icon: Zap,    testid: "more-quick-assign" },
      ],
    });
  }

  // Every role gets their own personal shifts / leave / swaps here as
  // additional discoverability (also lives on primary rail already).
  const myItems = [];
  if (caps.isPlanner) {
    // Planners: my-work already lives on the desktop rail; keep the
    // same items here so mobile parity is high.
    myItems.push(
      { to: "/my-leave",        label: t("my_leave"),        icon: ClipboardList, testid: "more-my-leave" },
      { to: "/my-swaps",        label: t("my_swaps"),        icon: Repeat,        testid: "more-my-swaps" },
      { to: "/my-availability", label: t("my_availability"), icon: Clock,         testid: "more-my-availability" },
    );
  }
  if (caps.isEmployee) {
    myItems.push(
      { to: "/my-swaps",        label: t("my_swaps"),        icon: Repeat, testid: "more-my-swaps" },
      { to: "/my-availability", label: t("my_availability"), icon: Clock,  testid: "more-my-availability" },
    );
  }
  if (myItems.length) {
    groups.push({ key: "my_work", section: t("navsec_my_work"), items: myItems });
  }

  // Communication that isn't in the primary bar
  const commsItems = [
    { to: "/news",          label: t("news"),          icon: Megaphone, testid: "more-news" },
    { to: "/notifications", label: t("notifications"), icon: Bell,      testid: "more-notifications" },
    { to: "/handbook",      label: t("handbook"),      icon: BookOpen,  testid: "more-handbook", handbookNewBadge: true },
  ];
  groups.push({ key: "communication", section: t("navsec_communication"), items: commsItems });

  // Personal — always available for every role. Company admin only
  // for owner (Settings/Billing) — capability-gated.
  const personal = [
    { to: "/profile",     label: t("profile"),           icon: User,        testid: "more-profile" },
    { to: "/preferences", label: t("preferences_label"), icon: SettingsIcon, testid: "more-preferences" },
  ];
  if (caps.canSettings) {
    personal.push({ to: "/settings", label: t("settings"),      icon: SettingsIcon, testid: "more-settings" });
  }
  if (caps.canBilling) {
    personal.push({ to: "/billing",  label: t("billing_label"), icon: CreditCard,   testid: "more-billing" });
  }
  groups.push({ key: "account", section: t("navsec_account"), items: personal });

  return groups;
}
